import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, CalendarClock, Clock, GitBranch, Handshake, Inbox,
  Layers, PhoneOff, RefreshCw, Sparkles, ListTodo, CalendarX,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { TaskPriorityBadge } from "@/components/TaskBadge";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { useMasterList } from "@/hooks/useMasterData";

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="border border-dashed border-slate-200 rounded p-6 text-center text-sm text-slate-500">
      <Icon className="h-6 w-6 mx-auto text-slate-300" />
      <div className="mt-1.5">{text}</div>
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-md border border-slate-200 bg-[#FEFDFB] px-5 py-4" data-testid="kpi-card">
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-3xl lg:text-4xl text-slate-900 mt-2 leading-none">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function useOverview() {
  return useQuery({
    queryKey: ["dashboard-overview"],
    queryFn: async () => (await api.get("/dashboard/overview")).data,
    refetchInterval: 60_000,
  });
}

function MetricCards() {
  const { data, isLoading } = useOverview();
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }
  const m = data?.metrics || {};
  const leadAccess = data?.role?.lead_access;
  const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
  const cards = [];
  if (leadAccess) {
    cards.push({ key: "total-leads", label: "Total Leads", value: fmt(m.total_leads) });
    cards.push({ key: "total-clients", label: "Total Clients", value: fmt(m.total_clients), sub: "institutional" });
    cards.push({ key: "connected-leads", label: "Total Connected Leads", value: fmt(m.connected_leads), sub: "≥1 activity logged" });
    cards.push({ key: "uncontacted-leads", label: "Total Uncontacted Leads", value: fmt(m.uncontacted_leads), sub: "no activity logged" });
  }
  cards.push({ key: "total-assets", label: "Total Number of Assets", value: fmt(m.total_assets), sub: `${fmt(m.archived_assets || 0)} archived` });
  if (leadAccess) cards.push({ key: "hot-warm", label: "Total Hot & Warm", value: fmt(m.hot_warm_clients), sub: "leads + clients" });
  cards.push({ key: "active-brokers", label: "Total Active Brokers", value: fmt(m.active_brokers) });

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-metric-cards">
      {cards.map((c) => (
        <div key={c.key} data-testid={`metric-${c.key}`}>
          <KpiCard label={c.label} value={c.value} sub={c.sub} />
        </div>
      ))}
    </div>
  );
}

// Shows top 4 items; a "See more" link opens a dialog with the full list.
function ListWidget({ icon: Icon, iconCls, title, items, empty, render, testid, className = "" }) {
  const [open, setOpen] = useState(false);
  const list = items || [];
  const visible = list.slice(0, 4);
  return (
    <div className={`bg-white border border-slate-200 rounded-md p-5 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2"><Icon className={`h-4 w-4 ${iconCls}`} /><div className="label-eyebrow">{title}</div></div>
      <div className="mt-3 space-y-1.5">
        {list.length === 0 ? <EmptyState icon={Icon} text={empty} /> : visible.map(render)}
      </div>
      {list.length > 4 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 text-xs font-semibold text-[#B8935A] hover:underline"
          data-testid={`${testid}-see-more`}
        >
          See more ({list.length})
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className={`h-4 w-4 ${iconCls}`} />{title} ({list.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {open && list.map(render)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActionCenter() {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role];

  const { data, isLoading } = useQuery({
    queryKey: ["action-center"],
    queryFn: async () => (await api.get("/dashboard/action-center")).data,
    refetchInterval: 30_000,
  });
  const { data: ov } = useOverview();

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const d = data || {};
  const ac = ov?.action_center || {};
  const leadAccess = ov?.role?.lead_access;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {leadAccess && (
        <div className="lg:col-span-2 flex items-center gap-2 pt-1" data-testid="ac-subheader-leads">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Leads</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
      )}
      {/* Uncontacted Leads — oldest-created first */}
      {leadAccess && (
        <ListWidget
          icon={PhoneOff} iconCls="text-rose-600" title="Uncontacted Leads"
          items={ac.uncontacted_leads} empty="Every lead has been contacted" testid="widget-uncontacted-leads"
          render={(l, i) => (
            <Link key={l.lead_id} to={`/leads/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`uncontacted-${i}`}>
              <div className="font-mono-num text-[10px] text-slate-500 w-20">{l.lead_code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{l.company_name || "—"} · {l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* Leads Due Today — highest warmth first */}
      {leadAccess && (
        <ListWidget
          icon={CalendarClock} iconCls="text-blue-600" title="Leads Due Today"
          items={ac.leads_due_today} empty="No leads due today" testid="widget-leads-due-today"
          render={(l, i) => (
            <Link key={l.lead_id} to={`/leads/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`due-today-${i}`}>
              <div className="font-mono-num text-[10px] text-slate-500 w-20">{l.lead_code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{l.company_name || "—"} · {l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* Overdue Follow-ups — most overdue first */}
      {leadAccess && (
        <ListWidget
          icon={Clock} iconCls="text-red-600" title="Overdue Follow-ups"
          items={d.overdue_followups} empty="All caught up — no overdue follow-ups" testid="widget-overdue-followups"
          render={(l, i) => (
            <Link key={i} to={`/leads/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`overdue-${i}`}>
              <div className="font-mono-num text-xs font-bold w-12 text-right text-red-700">{l.days_overdue}d</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500">{l.last_activity_outcome || "no activity"} · {l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* ---- Clients group ---- */}
      {leadAccess && (
        <div className="lg:col-span-2 flex items-center gap-2 pt-1" data-testid="ac-subheader-clients">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Clients</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
      )}
      {leadAccess && (
        <ListWidget
          icon={PhoneOff} iconCls="text-rose-600" title="Uncontacted Clients"
          items={ac.uncontacted_clients} empty="Every client has been contacted" testid="widget-uncontacted-clients"
          render={(l, i) => (
            <Link key={l.lead_id} to={`/clients/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`uncontacted-client-${i}`}>
              <div className="font-mono-num text-[10px] text-slate-500 w-20">{l.lead_code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}
      {leadAccess && (
        <ListWidget
          icon={CalendarClock} iconCls="text-blue-600" title="Clients Due Today"
          items={ac.clients_due_today} empty="No clients due today" testid="widget-clients-due-today"
          render={(l, i) => (
            <Link key={l.lead_id} to={`/clients/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`client-due-today-${i}`}>
              <div className="font-mono-num text-[10px] text-slate-500 w-20">{l.lead_code}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}
      {leadAccess && (
        <ListWidget
          icon={Clock} iconCls="text-red-600" title="Overdue Client Follow-ups"
          items={d.overdue_client_followups} empty="All caught up — no overdue client follow-ups" testid="widget-overdue-client-followups"
          render={(l, i) => (
            <Link key={i} to={`/clients/${l.lead_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`overdue-client-${i}`}>
              <div className="font-mono-num text-xs font-bold w-12 text-right text-red-700">{l.days_overdue}d</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{l.lead_name}</div>
                <div className="text-[10px] text-slate-500">{l.last_activity_outcome || "no activity"} · {l.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* ---- Brokers & Ops ---- */}
      {leadAccess && (
        <div className="lg:col-span-2 flex items-center gap-2 pt-1" data-testid="ac-subheader-ops">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Brokers &amp; Operations</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
      )}
      {/* Brokers Due Today */}
      <ListWidget
        icon={Handshake} iconCls="text-cyan-600" title="Brokers Due Today"
        items={ac.brokers_due_today} empty="No broker follow-ups due today" testid="widget-brokers-due-today"
        render={(b, i) => (
          <Link key={i} to={`/brokers/${b.broker_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`broker-today-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-slate-900">{b.label}</div>
              <div className="text-[10px] text-slate-500">{b.kind === "Share" ? "Share follow-up" : (b.kind === "MeetingAsset" || b.kind === "MeetingAssetGroup") ? "Meeting follow-up" : "Relationship follow-up"}</div>
            </div>
          </Link>
        )}
      />

      {/* Broker Follow-ups Overdue — most overdue first */}
      <ListWidget
        icon={Handshake} iconCls="text-violet-600" title="Broker Follow-ups Overdue"
        items={d.broker_followups} empty="No overdue broker follow-ups" testid="widget-broker-followups"
        render={(b, i) => (
          <Link key={i} to={`/brokers/${b.broker_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`broker-fu-${i}`}>
            <div className="font-mono-num text-xs font-bold w-12 text-right text-violet-700">{b.days_overdue}d</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-slate-900">{b.label}</div>
              <div className="text-[10px] text-slate-500">{b.kind === "Share" ? "Share follow-up" : (b.kind === "MeetingAsset" || b.kind === "MeetingAssetGroup") ? "Meeting follow-up" : "Relationship follow-up"}</div>
            </div>
          </Link>
        )}
      />

      {/* SLA Breaches — longest-in-stage first */}
      {leadAccess && (
        <ListWidget
          icon={AlertTriangle} iconCls="text-orange-600" title="SLA Breaches"
          items={d.sla_breaches} empty="No SLA breaches — pipeline moving well" testid="widget-sla-breaches"
          render={(m, i) => (
            <Link key={i} to="/kanban" className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`sla-${i}`}>
              <div className="font-mono-num text-xs font-bold w-12 text-right text-orange-700">{m.days_stuck}d</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{m.asset_name} → {m.lead_name}</div>
                <div className="text-[10px] text-slate-500">{m.stage} · Owner: {m.owner_name || "—"}</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* Strong Matches Awaiting Action — highest score first */}
      {leadAccess && (
        <ListWidget
          icon={Sparkles} iconCls="text-emerald-600" title="Strong Matches Awaiting Action"
          items={d.strong_matches} empty="No new strong matches to share" testid="widget-strong-matches"
          render={(m, i) => (
            <Link key={i} to="/kanban" className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`sm-${i}`}>
              <div className="font-mono-num text-sm font-bold w-12 text-right text-emerald-700">{m.match_score}%</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-slate-900">{m.asset_name} → {m.lead_name}</div>
                <div className="text-[10px] text-slate-500">{m.days_since_created}d since suggested</div>
              </div>
            </Link>
          )}
        />
      )}

      {/* Tasks Due Today — High priority first */}
      <ListWidget
        icon={ListTodo} iconCls="text-amber-600" title="Tasks Due Today"
        items={d.tasks_due_today} empty="No tasks due today" testid="widget-tasks-due-today"
        render={(t, i) => (
          <Link key={t.task_id} to={`/tasks/${t.task_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`task-due-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-slate-900">{t.title}</div>
              <div className="text-[10px] text-slate-500 truncate">{t.linked_record_name ? `${t.linked_record_type}: ${t.linked_record_name}` : "No linked record"}</div>
            </div>
            <TaskPriorityBadge priority={t.priority} />
          </Link>
        )}
      />

      {/* Overdue Tasks — most overdue first */}
      <ListWidget
        icon={CalendarX} iconCls="text-red-600" title="Overdue Tasks"
        items={d.tasks_overdue} empty="No overdue tasks" testid="widget-tasks-overdue"
        render={(t, i) => (
          <Link key={t.task_id} to={`/tasks/${t.task_id}`} className="flex items-center gap-3 p-2 rounded border border-slate-200 hover:bg-slate-50" data-testid={`task-overdue-${i}`}>
            <div className="font-mono-num text-xs font-bold w-12 text-right text-red-700">{t.days_overdue}d</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-slate-900">{t.title}</div>
              <div className="text-[10px] text-slate-500 truncate">{t.linked_record_name ? `${t.linked_record_type}: ${t.linked_record_name}` : "No linked record"}</div>
            </div>
            <TaskPriorityBadge priority={t.priority} />
          </Link>
        )}
      />
    </div>
  );
}

function LeadGroupSnapshot() {
  const { data } = useOverview();
  if (!data?.role?.lead_access) return null;
  const groups = data?.lead_groups || [];
  return (
    <div className="mt-8">
      <div className="label-eyebrow text-slate-700 mb-3">Lead Group Snapshot</div>
      {groups.length === 0 ? (
        <EmptyState icon={Layers} text="No lead groups yet" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lead-group-snapshot">
          {groups.map((g) => (
            <Link
              key={g.id}
              to={`/lead-groups/${g.id}/kanban`}
              className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-md transition-shadow flex items-center justify-between"
              data-testid={`group-snapshot-${g.id}`}
            >
              <div className="min-w-0 pr-3">
                <div className="text-sm font-semibold text-slate-900 truncate">{g.name}</div>
                <div className="text-[11px] text-[#B8935A] font-semibold mt-0.5">Open Kanban →</div>
              </div>
              <div className="font-display text-3xl text-slate-900 leading-none">{g.lead_count}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineFunnelChart() {
  const { data } = useOverview();
  const { data: statuses = [] } = useMasterList("match_status");
  if (!data?.role?.lead_access) return null;
  const funnel = data?.funnel || {};
  const colorByLabel = Object.fromEntries(statuses.map((s) => [s.label, s.badge_color]));
  const chartData = Object.entries(funnel).map(([stage, count]) => ({ stage, count, color: colorByLabel[stage] }));
  const total = chartData.reduce((a, b) => a + b.count, 0);
  return (
    <div className="mt-8" data-testid="pipeline-funnel-section">
      <div className="label-eyebrow text-slate-700 mb-3">Pipeline Funnel — Matches by Status</div>
      <div className="bg-white border border-slate-200 rounded-md p-5">
        {total === 0 ? (
          <EmptyState icon={GitBranch} text="No matches in the pipeline yet" />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-22} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.color || "#0f172a"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const runAuto = async () => {
    await api.post("/notifications/run-automations");
    toast.success("Automations re-run; dashboard refreshed");
    window.location.reload();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Command Center</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1.5">Today's queue and pipeline health — all in one place.</p>
        </div>
        <Button variant="outline" size="sm" onClick={runAuto} data-testid="dashboard-run-automations">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Run automations
        </Button>
      </div>

      {/* Metric cards row */}
      <div className="mt-6">
        <MetricCards />
      </div>

      {/* Action Center */}
      <div className="mt-8">
        <div className="label-eyebrow text-slate-700 mb-3">Action Center</div>
        <ActionCenter />
      </div>

      {/* Lead Group Snapshot */}
      <LeadGroupSnapshot />

      {/* Pipeline Funnel — at end of page */}
      <PipelineFunnelChart />
    </div>
  );
}
